import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { submitStatement } from '../api';
import { BANKS } from '../banks';
import { toApiError } from '../errors';
import { useJourney } from '../journey';
import { DETAILS_EDITABLE } from '../screens';
import Button from '../ui/Button';
import { SearchSelect } from '../ui/Fields';
import FileDrop, { ReadFile } from '../ui/FileDrop';
import { Notice, SectionTitle } from '../ui/Parts';
import Shell from '../ui/Shell';

const BANK_OPTIONS = BANKS.map((b) => ({ value: b.bankCode, label: b.bankName }));

const BankStatement = () => {
  const { steps, current, session, setSession, updateUi, close } = useJourney();
  const [bankCode, setBankCode] = useState(session.statement?.bankCode ?? '');
  const [file, setFile] = useState<ReadFile | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const failed = session.status === 'STATEMENT_ANALYSIS_FAILED' || session.statement?.status === 'FAILED';
  const canGoBack = DETAILS_EDITABLE.has(session.status);

  const submit = async () => {
    if (!bankCode || !file) {
      setError(!bankCode ? 'Choose the bank that issued the statement.' : 'Upload your bank statement.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      setSession(await submitStatement({ bankCode, contentBase64: file.contentBase64, fileName: file.file.name }));
    } catch (e) {
      setError(toApiError(e).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell
      steps={steps}
      current={current}
      onClose={close}
      footer={
        <div className={`grid gap-4 ${canGoBack ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {canGoBack && (
            <Button variant="secondary" onClick={() => updateUi({ editingDetails: true })} disabled={submitting}>
              <ChevronLeft className="h-5 w-5" aria-hidden /> Back
            </Button>
          )}
          <Button onClick={submit} loading={submitting} disabled={!bankCode || !file}>
            Continue
          </Button>
        </div>
      }
    >
      <SectionTitle title="Bank Statement" subtitle="We need your bank statement to assess your eligibility for financing." />
      {failed && session.statement?.failureReason && (
        <Notice>{`We couldn't use your last statement: ${session.statement.failureReason} Please upload another.`}</Notice>
      )}
      {error && <Notice>{error}</Notice>}
      <div className="space-y-5">
        <SearchSelect
          id="bank"
          label="Bank"
          placeholder="Select the bank that issued the statement"
          options={BANK_OPTIONS}
          value={bankCode}
          onChange={setBankCode}
          disabled={submitting}
        />
        <div>
          <p className="text-sm md:text-base font-medium mb-2">Upload Bank Statement</p>
          <FileDrop value={file} onChange={setFile} disabled={submitting} />
        </div>
      </div>
    </Shell>
  );
};

export default BankStatement;
